import { config } from "../config";
import { callGateway, type CallerIdentity } from "./gateway";
import type { AnkhInvoice } from "./ankhInvoices";

/**
 * Ankh Billing › Customers (owner 2026-10-23): Ankh's OWN customer ledger in
 * io.ankhos. radaccess v1.135+ (handler/ankhcustomers.go), superadmin only,
 * and every route refuses any namespace but io.ankhos.
 *
 *   GET  /billing/io.ankhos/customers                          operators + their io.ankhos account
 *   POST /billing/io.ankhos/customers/{OperatorNS}/account     open it, identity copied from the operator's profile
 *   POST /billing/io.ankhos/accounts                           open a company account typed in once
 *   POST /billing/io.ankhos/accounts/{acct}/invoices           create a draft
 *   GET  /billing/io.ankhos/accounts/{acct}/invoices/{id}      the draft with its lines and totals
 *   POST /billing/io.ankhos/accounts/{acct}/invoices/{id}/lines
 *   PUT  /billing/io.ankhos/accounts/{acct}/invoices/{id}/status  Generated (issue) or Cancelled
 *
 * 🔑 Every amount shown is radbilling's own arithmetic, read back after each
 * step -- the console never computes tax.
 */

export const ANKH_SELLER = "io.ankhos";

const enc = encodeURIComponent;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

function uuid(id: string, what: string): string {
  if (!UUID.test(id)) throw new Error(`Not ${what}: ${id}`);
  return id;
}

const base = `/billing/${ANKH_SELLER}`;

export interface AnkhCustomer {
  namespace: string;
  legalname: string;
  operatortype: string;
  active: boolean;
  gstin?: string;
  registrationstatus?: string;
  address?: string;
  city?: string;
  state?: string;
  pin?: string;
  billingemail?: string;
  billingphone?: string;
  /** "live" or "test" -- radbilling_invoice_mode; test numbers TO/TI and is never filed. */
  invoicemode: string;
  accountid: string;
  accountopen: boolean;
  /** What the operator's own Edit profile still lacks for a claimable tax invoice. */
  missing: string[];
}

/** A kind of io.ankhos customer with no operator namespace; the gateway owns the list. */
export interface AnkhCustomerType {
  code: string;
  label: string;
}

/** Used only when an older gateway sends no list. */
export const DEFAULT_CUSTOMER_TYPES: AnkhCustomerType[] = [{ code: "billing_only", label: "Billing-only company" }];

export interface AnkhCustomerList {
  customers: AnkhCustomer[];
  customertypes: AnkhCustomerType[];
}

export async function listAnkhCustomers(identity: CallerIdentity, signal?: AbortSignal): Promise<AnkhCustomerList> {
  const r = await callGateway<{ customers: AnkhCustomer[] | null; customertypes?: AnkhCustomerType[] | null }>(
    identity,
    config().gatewayBase,
    `${base}/customers`,
    signal ? { signal } : {},
  );
  const types = r.data?.customertypes;
  return { customers: r.data?.customers ?? [], customertypes: types && types.length ? types : DEFAULT_CUSTOMER_TYPES };
}

export interface OpenedAccount {
  accountid: string;
  /** Non-empty when the account opened but something secondary (the email) did not save. */
  warning?: string;
}

export async function openOperatorAccount(identity: CallerIdentity, operatorNs: string): Promise<OpenedAccount> {
  const r = await callGateway<OpenedAccount>(identity, config().gatewayBase, `${base}/customers/${enc(operatorNs)}/account`, {
    method: "POST",
    body: {},
  });
  return r.data;
}

export interface CompanyAccountInput {
  customertype: string;
  legalname: string;
  gstin: string;
  address: string;
  city: string;
  state: string;
  pin: string;
  email: string;
  mobile: string;
}

/** 15 characters: state code, PAN, entity number, Z, check character. */
export const GSTIN_RE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;

export async function openCompanyAccount(identity: CallerIdentity, input: CompanyAccountInput): Promise<OpenedAccount> {
  const r = await callGateway<OpenedAccount>(identity, config().gatewayBase, `${base}/accounts`, {
    method: "POST",
    body: { ...input, gstin: input.gstin.trim().toUpperCase() },
  });
  return r.data;
}

/** India midnight of a yyyy-mm-dd date, as RFC3339 -- what radbilling stores a period in. */
export function istMidnight(day: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) throw new Error(`Not a date: ${day}`);
  return `${day}T00:00:00+05:30`;
}

export async function createDraftInvoice(identity: CallerIdentity, accountId: string, startDay: string, endDay: string): Promise<AnkhInvoice> {
  const r = await callGateway<AnkhInvoice>(
    identity,
    config().gatewayBase,
    `${base}/accounts/${uuid(accountId, "an account id")}/invoices`,
    { method: "POST", body: { start: istMidnight(startDay), end: istMidnight(endDay) } },
  );
  return r.data;
}

export async function getAnkhInvoice(identity: CallerIdentity, accountId: string, invoiceId: string, signal?: AbortSignal): Promise<AnkhInvoice> {
  const r = await callGateway<AnkhInvoice>(
    identity,
    config().gatewayBase,
    `${base}/accounts/${uuid(accountId, "an account id")}/invoices/${uuid(invoiceId, "an invoice id")}`,
    signal ? { signal } : {},
  );
  return r.data;
}

export interface InvoiceLineInput {
  componentid: string;
  quantity: number;
  /** Net of GST. Omit to use the price list. */
  unitprice?: number;
  discountamount?: number;
  /** Required by radbilling whenever the price is below the price list. */
  discountreason?: string;
  /** Printed under the item: the service period and what was supplied. */
  note?: string;
}

/** radbilling's MaxLineNoteLength. */
export const MAX_LINE_NOTE = 160;

/**
 * The last day of a period of `months` starting on `start` (yyyy-mm-dd):
 * 2026-10-01 + 6 months ends 2027-03-31. A start day the end month lacks is
 * clamped to that month's last day before stepping back one day.
 */
export function periodEnd(start: string, months: number): string {
  const [y = 1970, m = 1, d = 1] = start.split("-").map(Number);
  const target = new Date(Date.UTC(y, m - 1 + months, 1));
  const lastDay = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate();
  target.setUTCDate(Math.min(d, lastDay));
  return new Date(target.getTime() - 86400_000).toISOString().slice(0, 10);
}

/**
 * Whether a typed price needs a reason. radbilling asks for one only when the
 * line is priced BELOW the price list; a plan priced per agreement (list price
 * 0, e.g. co-location) never needs one.
 */
export function priceNeedsReason(unitprice: string, listPrice: number | undefined): boolean {
  if (unitprice.trim() === "") return false;
  const p = Number(unitprice);
  return Number.isFinite(p) && listPrice !== undefined && p < listPrice;
}

export async function addInvoiceLine(identity: CallerIdentity, accountId: string, invoiceId: string, line: InvoiceLineInput): Promise<void> {
  await callGateway<unknown>(
    identity,
    config().gatewayBase,
    `${base}/accounts/${uuid(accountId, "an account id")}/invoices/${uuid(invoiceId, "an invoice id")}/lines`,
    { method: "POST", body: { ...line, componentid: uuid(line.componentid, "a component id") } },
  );
}

/** Issue (allocates the next number, stores the PDF) or withdraw a draft. */
export async function setInvoiceStatus(
  identity: CallerIdentity,
  accountId: string,
  invoiceId: string,
  status: "Generated" | "Cancelled",
): Promise<void> {
  await callGateway<unknown>(
    identity,
    config().gatewayBase,
    `${base}/accounts/${uuid(accountId, "an account id")}/invoices/${uuid(invoiceId, "an invoice id")}/status`,
    { method: "PUT", body: { status } },
  );
}

/** The dashboard's state for one operator, worst first. */
export function customerState(c: Pick<AnkhCustomer, "accountopen" | "missing" | "invoicemode">): "profile incomplete" | "no account" | "test" | "ready" {
  if (c.missing.length > 0 && !c.accountopen) return "profile incomplete";
  if (!c.accountopen) return "no account";
  if (c.invoicemode !== "live") return "test";
  return "ready";
}

/**
 * Named contacts of an account (owner 2026-11-04, radaccess v1.266+): owner,
 * accountant and more. Never deleted -- Remove sets active false.
 */
export const CONTACT_ROLES = [
  { code: "owner", label: "Owner" },
  { code: "accountant", label: "Accountant" },
  { code: "billing", label: "Billing" },
  { code: "technical", label: "Technical" },
  { code: "other", label: "Other" },
] as const;

export interface ContactInput {
  role: string;
  name: string;
  email: string;
  mobile: string;
  address: string;
  city: string;
  state: string;
  pin: string;
  receivesinvoices: boolean;
  active?: boolean;
}

export interface Contact extends ContactInput {
  id: string;
  active: boolean;
  createdby: string;
  createdat: string;
}

export const EMPTY_CONTACT: ContactInput = {
  role: "owner", name: "", email: "", mobile: "", address: "", city: "", state: "", pin: "", receivesinvoices: true,
};

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/** What the gateway will refuse, said before sending (radaccess contactInput.contact). */
export function contactProblems(c: ContactInput): string[] {
  const p: string[] = [];
  const name = c.name.trim() || "This contact";
  const email = c.email.trim();
  const digits = c.mobile.replace(/\D/g, "").replace(/^(91|0)(?=\d{10}$)/, "");
  if (!c.name.trim()) p.push("Name is required");
  if (email && !EMAIL_RE.test(email)) p.push(`${name}: the email is not valid`);
  if (c.mobile.trim() && !/^[6-9]\d{9}$/.test(digits)) p.push(`${name}: the mobile is not a 10-digit Indian mobile`);
  if (!email && !c.mobile.trim()) p.push(`${name} needs an email or a mobile`);
  if (c.receivesinvoices && !email) p.push(`${name} cannot receive invoices without an email`);
  return p;
}

export async function listContacts(identity: CallerIdentity, accountId: string, signal?: AbortSignal): Promise<Contact[]> {
  const r = await callGateway<Contact[] | null>(identity, config().gatewayBase, `${base}/accounts/${enc(accountId)}/contacts`, signal ? { signal } : {});
  return r.data ?? [];
}

export async function addContact(identity: CallerIdentity, accountId: string, c: ContactInput): Promise<Contact> {
  const r = await callGateway<Contact>(identity, config().gatewayBase, `${base}/accounts/${enc(accountId)}/contacts`, { method: "POST", body: c });
  return r.data;
}

export async function updateContact(identity: CallerIdentity, accountId: string, id: string, c: ContactInput & { active: boolean }): Promise<Contact> {
  const r = await callGateway<Contact>(identity, config().gatewayBase, `${base}/accounts/${enc(accountId)}/contacts/${enc(id)}`, {
    method: "PUT",
    body: c,
  });
  return r.data;
}
